import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import API from "../api/api";
import { setUser } from "../store/userSlice";
import { DeptSelector, getDepartmentEmoji } from "../components/Popups";
import { useLanguage, getTranslation } from "../utils/language";
import "../styles/edit-account.css";

export default function EditAccount() {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [language] = useLanguage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [name, setName] = useState("");

  // store ids
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);

  // store names only for UI
  const [selectedDeptNames, setSelectedDeptNames] = useState([]);

  const [showDeptSelector, setShowDeptSelector] = useState(false);

  const [profileName, setProfileName] = useState("");

  useEffect(() => {

    const fetchData = async () => {

      try {

        const profileRes = await API.get("/user/my-account");
        const p = profileRes.data;

        setName(p.name || "");
        setProfileName(p.name || "");

        if (Array.isArray(p.departments)) {

         const ids = (p.departments || []).map(d => d._id);
const names = (p.departments || []).map(d => d.name);

          setSelectedDeptIds(ids);
          setSelectedDeptNames(names);
        }

      } catch {

        setError("Unable to load data.");

      } finally {

        setLoading(false);

      }
    };

    fetchData();

  }, []);

  // DeptSelector now returns (selectedIds, selectedDeptObjects)
  const handleDeptDone = (selectedIds, selectedDepts = []) => {
    setShowDeptSelector(false);
    if (!selectedIds || selectedIds.length === 0) return;

    setSelectedDeptIds(selectedIds);
    setSelectedDeptNames(selectedDepts.map((d) => d.name));
  };


  const handleSubmit = async (e) => {

    e.preventDefault();

    setError("");
    setSuccess("");

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name cannot be empty.");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Name cannot exceed 100 characters.");
      return;
    }
    if (!/^[\p{L}\p{M}\s\-'.]+$/u.test(trimmedName)) {
      setError("Name can only contain letters, spaces, hyphens, and apostrophes.");
      return;
    }

    try {

      setSaving(true);

      const res = await API.patch("/user/edit-account", {
        name,
        departments: selectedDeptIds
      });

      dispatch(setUser(res.data.user));

      setSuccess("Profile updated successfully!");

      setTimeout(() => navigate("/account"), 1200);

    } catch (err) {

      setError(err.response?.data?.message || "Unable to update.");

    } finally {

      setSaving(false);

    }
  };

  if (loading) {

    return (
      <div className="ea-loader-wrap">
        <div className="ea-spinner" />
        <p>Loading…</p>
      </div>
    );

  }

  return (
    <div className="ea-page">

      <div className="ea-wrap">

        <button
          className="ea-back-btn"
          onClick={() => navigate("/account")}
        >
          ← {getTranslation(language, "account", "back")}
        </button>

        <h1 className="ea-title">✏️ {getTranslation(language, "account", "editProfileTitle")}</h1>

        <form
          className="ea-form"
          onSubmit={handleSubmit}
        >

          {/* NAME */}

          <section className="ea-card">

            <h3 className="ea-card-title">👤 {getTranslation(language, "account", "nameLabel")}</h3>

            <div className="ea-field">

              <label>{getTranslation(language, "account", "nameLabel")}</label>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={getTranslation(language, "account", "yourName")}
                required
              />

            </div>

          </section>

          {/* DEPARTMENTS */}

          <section className="ea-card">

            <h3 className="ea-card-title">
              🏢 {getTranslation(language, "account", "deptPref")}
            </h3>

            {selectedDeptNames.length === 0 ? (

              <p className="ea-dept-hint">
                {getTranslation(language, "account", "noDept")}
              </p>

            ) : (

              <div className="ea-dept-grid">

                {selectedDeptNames.map((d) => (

                  <span
                    key={d}
                    className="ea-dept-chip ea-dept-chip--active"
                  >
                    <span>{getDepartmentEmoji(d)}</span>
                    <span>{d}</span>
                  </span>

                ))}

              </div>

            )}

            <button
              type="button"
              className="ea-dept-change-btn"
              onClick={() => setShowDeptSelector(true)}
            >

              {selectedDeptNames.length === 0
                ? getTranslation(language, "account", "selectDept")
                : `✏️ ${getTranslation(language, "account", "changeDept")}`}

            </button>

          </section>

          {error && (
            <p className="ea-error">{error}</p>
          )}

          {success && (
            <p className="ea-success">{getTranslation(language, "account", "profileUpdated")}</p>
          )}

          <button
            type="submit"
            className="ea-submit-btn"
            disabled={saving}
          >

            {saving ? getTranslation(language, "account", "saving") : getTranslation(language, "account", "saveChanges")}

          </button>

        </form>

      </div>

      {showDeptSelector && (
  <DeptSelector
    userName={profileName}
    preSelected={selectedDeptIds}
    onDone={handleDeptDone}
    onClose={() => setShowDeptSelector(false)}   // ✅ THIS FIX
  />
)}

    </div>
  );
}
